import React, { ComponentProps, DOMAttributes } from 'react'
import * as R from 'remeda'
import { FocusableElement } from '@react-types/shared'
import { AriaButtonProps } from 'react-aria'
import {
  SvgCaution,
  SvgInform,
  SvgRemove,
  SvgSuccess,
  SvgWarning,
} from '@chilipiper/icons/src/design-system'
import invariant from 'tiny-invariant'
import styled, { css } from 'styled-components'
import { typeValidator } from '@chilipiper/utils'
import { Box, Flex, Text } from '../../../core-components'
import { ActionBarButton } from '../../action/action-bar/action-bar-button/ActionBarButton'
import { ActionBar } from '../../action'
import { ThemeColors, ThemeShadows } from '../../../theme'
import { StartContent, StartContentProvider } from '../../data/start-content/StartContent'

type Actions = Array<{
  'data-test-id'?: string
  onClick: () => void
  text: string
  variant: ComponentProps<typeof ActionBarButton>['variant']
}>

type Props = (
  | {
      actions?: Actions
      'data-test-id'?: string
      onClose?: () => void
      type?: 'inline'
      actionsPlacement?: 'bottom' | 'right'
    }
  | {
      actions?: Actions
      closeButtonProps: AriaButtonProps<'button'>
      onClose: () => void
      titleProps: DOMAttributes<FocusableElement>
      contentProps: DOMAttributes<FocusableElement>
      descriptionProps: DOMAttributes<FocusableElement>
      type: 'floating'
      actionsPlacement?: never
    }
) & {
  description?: string
  isDismissable?: boolean
  startContent?: React.ReactNode
  title: string
  variant: 'neutral' | 'success' | 'inform' | 'warning' | 'caution'
}

const AlertBannerStartContent = (props: React.ComponentPropsWithoutRef<typeof StartContent>) => (
  <StartContent {...props} />
)

export const AlertBanner = (props: Props) => {
  invariant(
    props.startContent === undefined || typeValidator(AlertBannerStartContent)(props.startContent),
    'startContent must be a AlertBannerStartContent component or undefined'
  )
  const actionsPlacement =
    ('actionsPlacement' in props ? props.actionsPlacement : undefined) ?? 'bottom'
  const {
    contentContainerBg,
    closeButtonProps,
    titleProps,
    contentProps,
    descriptionProps,
    borderRadius,
    textColor,
    descriptionColor,
    boxShadow,
    dataTestId,
    bg,
  } = typeProps(props)
  return (
    <Box
      flexGrow={1}
      bg={bg}
      data-test-id={dataTestId}
      overflow='hidden'
      borderRadius={borderRadius}
      boxShadow={boxShadow}
      {...contentProps}
    >
      <Flex
        bg={contentContainerBg}
        boxShadow={
          props.actions !== undefined && actionsPlacement === 'bottom'
            ? props.type === 'floating'
              ? 'border/row-oncolor'
              : 'border/row'
            : undefined
        }
      >
        <Flex py={3} px={6} gap={3} flexGrow={1}>
          <StartContentProvider value={{ iconColor: iconColorMap[props.variant] }}>
            <Box mt={1} flexShrink={0}>
              {props.startContent ? (
                props.startContent
              ) : (
                <StartContent variant='icon' icon={iconMap[props.variant]} />
              )}
            </Box>
          </StartContentProvider>
          <Flex flexDirection='column' gap={1}>
            <Text textStyle='body-multiline' color={textColor} {...titleProps}>
              {props.title}
            </Text>
            {props.description && (
              <Text textStyle='body-multiline' color={descriptionColor} {...descriptionProps}>
                {props.description}
              </Text>
            )}
          </Flex>
        </Flex>
        {actionsPlacement === 'right' && (
          <ReverseColorScheme data-enabled={props.type === 'floating'}>
            <Flex
              boxShadow={
                props.type === 'floating' ? 'border/cell-left-on-color' : 'border/cell-left'
              }
            >
              <AlertBannerActions actions={props.actions} onClose={props.onClose} variant='inRow' />
            </Flex>
          </ReverseColorScheme>
        )}
        {props.isDismissable && (
          <Flex
            boxShadow={props.type === 'floating' ? 'border/cell-left-on-color' : 'border/cell-left'}
          >
            <ActionBar variant='inRow'>
              <ActionBar.IconButton
                label='Close'
                icon={SvgRemove}
                onClick={props.onClose ?? R.doNothing()}
                ariaProps={closeButtonProps}
              />
            </ActionBar>
          </Flex>
        )}
      </Flex>
      {actionsPlacement === 'bottom' && (
        <ReverseColorScheme data-enabled={props.type === 'floating'}>
          <AlertBannerActions actions={props.actions} onClose={props.onClose} variant='nested' />
        </ReverseColorScheme>
      )}
    </Box>
  )
}

const ReverseColorScheme = styled.div<{ 'data-enabled': boolean }>`
  display: contents;
  ${({ 'data-enabled': enabled }) => {
    if (!enabled) {
      return undefined
    }
    return css`
      [data-theme='dark']:not(:has([data-theme])) &[data-enabled='true'] {
        color-scheme: light;
      }

      [data-theme='light']:not(:has([data-theme])) &[data-enabled='true'] {
        color-scheme: dark;
      }
    `
  }}
`

type AlertBannerActionsProps = {
  actions?: Actions
  onClose?: () => void
  variant: 'nested' | 'inRow'
}

const AlertBannerActions = ({ actions, onClose, variant }: AlertBannerActionsProps) => {
  if (!actions) {
    return null
  }

  return (
    <ActionBar variant={variant}>
      {actions.map(actionObject => (
        <ActionBar.Button
          key={actionObject.text}
          onClick={() => {
            actionObject.onClick()
            onClose?.()
          }}
          variant={actionObject.variant}
          data-test-id={actionObject['data-test-id']}
        >
          {actionObject.text}
        </ActionBar.Button>
      ))}
    </ActionBar>
  )
}

const variantBg = {
  neutral: 'bg/section-emphasis',
  success: 'bg/success',
  inform: 'bg/inform',
  warning: 'bg/warning',
  caution: 'bg/caution',
} satisfies Record<Props['variant'], ThemeColors>

const iconColorMap = {
  inform: 'fg/inform',
  warning: 'fg/warning',
  success: 'fg/success',
  caution: 'fg/caution',
  neutral: 'fg/minor',
} as const

const iconMap = {
  inform: SvgInform,
  warning: SvgWarning,
  success: SvgSuccess,
  caution: SvgCaution,
  neutral: SvgInform,
}

const typeProps = (
  props: Props
): {
  bg?: ThemeColors
  boxShadow?: ThemeShadows
  borderRadius?: number
  dataTestId?: string
  textColor: ThemeColors
  descriptionColor: ThemeColors
  titleProps: DOMAttributes<FocusableElement>
  contentProps: DOMAttributes<FocusableElement>
  descriptionProps: DOMAttributes<FocusableElement>
  closeButtonProps?: AriaButtonProps<'button'>
  contentContainerBg?: ThemeColors
} => {
  if (props.type === 'floating') {
    return {
      bg: 'bg/accent',
      boxShadow: 'border/popover',
      borderRadius: 12,
      titleProps: props.titleProps,
      contentProps: props.contentProps,
      descriptionProps: props.descriptionProps,
      closeButtonProps: props.closeButtonProps,
      textColor: 'fg/major-on-color',
      descriptionColor: 'fg/moderate-on-color',
    }
  }
  return {
    dataTestId: props['data-test-id'],
    contentContainerBg: variantBg[props.variant],
    textColor: 'fg/major',
    descriptionColor: 'fg/moderate',
    titleProps: {},
    contentProps: {},
    descriptionProps: {},
  }
}

AlertBanner.StartContent = AlertBannerStartContent
